'use client';

import { useId, useMemo, useState } from 'react';
import { searchCatalog, type Product } from '@roomtreatment/av-catalog';
import { optionCardClass, optionGlyphClass } from '@/components/room/chrome/selection';
import { TYPE_LABELS } from '@/components/room/gear/labels';
import { CONTENT, SECOND_LINE } from '@/components/seo/roles';
import { productLabel } from './checkerCopy';

interface Props {
  label: string;
  /** Products this role accepts; null while the catalogue loads. */
  products: readonly Product[] | null;
  selectedId: string | undefined;
  /** A role that may stay empty offers "None". */
  optional: boolean;
  onPick: (id: string | undefined) => void;
  testId: string;
}

/** A searchable single-select list of the products one role accepts. */
export default function ProductPicker({ label, products, selectedId, optional, onPick, testId }: Props) {
  const [query, setQuery] = useState('');
  const inputId = useId();
  const results = useMemo(() => (products ? searchCatalog(products, query, null) : []), [products, query]);

  if (!products) return <p className={SECOND_LINE}>Loading products…</p>;

  const option = (id: string | undefined, name: string, second: string | null) => {
    const active = id === selectedId;
    return (
      <li key={id ?? 'none'}>
        <button
          type="button"
          role="option"
          aria-selected={active}
          data-testid={`${testId}-option-${id ?? 'none'}`}
          onClick={() => onPick(id)}
          className={optionCardClass(active, 'w-full items-center')}
        >
          <span className={optionGlyphClass(active, 'dot')} aria-hidden />
          <span className="min-w-0">
            <span className={`block truncate ${CONTENT}`}>{name}</span>
            {second && <span className={`block ${SECOND_LINE}`}>{second}</span>}
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label htmlFor={inputId} className="sr-only">{`Search ${label.toLowerCase()}`}</label>
      <input
        id={inputId}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search brand or model"
        autoComplete="off"
        className={`w-full rounded-lg border border-[var(--app-border-bright)] bg-transparent px-3 py-2 placeholder:text-[var(--ed-text-faint)] ${CONTENT}`}
      />
      <ul role="listbox" aria-label={label} className="max-h-72 space-y-2 overflow-y-auto pr-1">
        {optional && !query && option(undefined, 'None', null)}
        {results.map((p) => option(p.id, productLabel(p), TYPE_LABELS[p.type]))}
        {results.length === 0 && <li className={SECOND_LINE}>No products match.</li>}
      </ul>
    </div>
  );
}
